import { ComposedCondition } from '@hyper-inferno/survey-elements-core';
import { isNumNotDefined, isOptionSelected } from './number-check';

const isEmptyObject = (val: any) =>
  val &&
  typeof val === 'object' &&
  !Array.isArray(val) &&
  Object.keys(val).length === 0;

export const OPERATOR_FUNCTIONS: Record<
  string,
  (userVal: any, cond: ComposedCondition) => boolean
> = {
  // --- Choice Logic ---
  // Expects userValue to be a number[] .option_key)
  // Is: checks if the array contains exactly this one key and nothing else
  is: (uv: number[], c) => uv.length === 1 && uv[0] === Number(c.option_key),

  // Is Not: true if the array doesn't contain the key, or contains other things too
  is_not: (uv, c) =>
    isOptionSelected(uv) &&
    Number(Array.isArray(uv) ? uv[0] : uv) !== Number(c.option_key),

  // Expects userValue to be an array of strings .option_keys)
  inc_any: (uv: number[], c) =>
    Array.isArray(uv) && uv.some((val) => c.option_keys.includes(val)),

  // Does Not Include Any: True only AFTER a selection is made, and NONE of those choices match the targets
  not_inc_any: (uv, c) => {
    // 1. Initial State Protection: If no options are selected yet, return false
    if (
      !isOptionSelected(uv) ||
      !Array.isArray(uv) ||
      uv.length === 0 ||
      !c.option_keys
    ) {
      return false;
    }

    // 2. Evaluate condition only after user interacts
    return !uv.some((val) => c.option_keys.includes(val));
  },

  inc_all: (uv, c) => {
    // Checks if the user's selected options (uv) include ALL keys in c.option_keys
    if (!Array.isArray(uv) || !c.option_keys) return false;
    return c.option_keys.every((key) => uv.includes(key));
  },

  // Is Any Of: Evaluates if at least one choice matches the target keys array
  is_any_of: (uv, c) => {
    if (!isOptionSelected(uv) || !c.option_keys) return false;
    const userVals = Array.isArray(uv) ? uv : [uv];
    return userVals.some((val) => c.option_keys.includes(Number(val)));
  },

  // Is Not Any Of: Evaluates if NONE of the choices overlap with the target keys array
  is_not_any_of: (uv, c) => {
    if (!isOptionSelected(uv) || !c.option_keys) return false;
    const userVals = Array.isArray(uv) ? uv : [uv];
    return !userVals.some((val) => c.option_keys.includes(Number(val)));
  },

  is_ex: (uv, c) => {
    // Checks if the user's selection is exactly the target set (order-independent)
    if (!Array.isArray(uv) || !c.option_keys) return false;
    return (
      uv.length === c.option_keys.length &&
      uv.every((val) => c.option_keys.includes(val))
    );
  },
  // Is Not Exactly: Strict inverse of is_ex (true if lengths mismatch or elements differ)
  is_not_ex: (uv, c) => {
    if (!isOptionSelected(uv) || !c.option_keys) return false;
    const userVals = Array.isArray(uv) ? uv : [uv];

    if (userVals.length !== c.option_keys.length) return true;
    return !userVals.every((val) => c.option_keys.includes(Number(val)));
  },

  count_eq: (uv, c) => {
    if (!Array.isArray(uv)) return false;
    const targetCount = Number(JSON.parse(c.value_manual || '0'));
    return uv.length === targetCount;
  },

  // Count Greater Than (>): True if the user selected more than the target number of options
  count_gt: (uv, c) => {
    if (!Array.isArray(uv)) return false;
    const targetCount = Number(JSON.parse(c.value_manual || '0'));
    return uv.length > targetCount;
  },

  count_gte: (uv, c) => {
    if (!Array.isArray(uv)) return false;
    const targetCount = Number(JSON.parse(c.value_manual || '0'));
    return uv.length >= targetCount;
  },

  // Count Less Than (<): True if a selection is made and the count is less than the target
  count_lt: (uv, c) => {
    if (!Array.isArray(uv) || uv.length === 0) return false; // Guard initial blank states
    const targetCount = Number(JSON.parse(c.value_manual || '0'));
    return uv.length < targetCount;
  },

  count_lte: (uv, c) => {
    if (!Array.isArray(uv) || uv.length === 0) return false; // Guard initial blank states
    const targetCount = Number(JSON.parse(c.value_manual || '0'));
    return uv.length <= targetCount;
  },

  // --- Text Logic ---
  // Case-insensitive comparisons are usually safer for surveys
  eq_text: (uv, c) =>
    String(uv || '').toLowerCase() ===
    String(JSON.parse(c.value_manual) || '').toLowerCase(),
  neq_text: (uv, c) => {
    if (uv === undefined || uv === null || String(uv).trim() === '')
      return false;
    return (
      String(uv).toLowerCase() !==
      String(JSON.parse(c.value_manual) || '').toLowerCase()
    );
  },
  cont: (uv, c) =>
    String(uv || '')
      .toLowerCase()
      .includes(String(JSON.parse(c.value_manual) || '').toLowerCase()),
  not_cont: (uv, c) => {
    if (uv === undefined || uv === null || String(uv).trim() === '')
      return false;
    return !String(uv)
      .toLowerCase()
      .includes(String(JSON.parse(c.value_manual) || '').toLowerCase());
  },
  sw: (uv, c) =>
    String(uv || '')
      .toLowerCase()
      .startsWith(String(JSON.parse(c.value_manual) || '').toLowerCase()),

  ew: (uv, c) =>
    String(uv || '')
      .toLowerCase()
      .endsWith(String(JSON.parse(c.value_manual) || '').toLowerCase()),

  // --- Numeric Logic ---
  eq_num: (uv, c) => {
    console.log(uv, c.value_manual, JSON.parse(c.value_manual));
    return (
      isNumNotDefined(uv) && Number(uv) === Number(JSON.parse(c.value_manual))
    );
  },
  neq_num: (uv, c) =>
    isNumNotDefined(uv) && Number(uv) !== Number(JSON.parse(c.value_manual)),
  gt: (uv, c) =>
    isNumNotDefined(uv) && Number(uv) > Number(JSON.parse(c.value_manual)),
  gte: (uv, c) =>
    isNumNotDefined(uv) && Number(uv) >= Number(JSON.parse(c.value_manual)),
  lt: (uv, c) =>
    isNumNotDefined(uv) && Number(uv) < Number(JSON.parse(c.value_manual)),
  lte: (uv, c) =>
    isNumNotDefined(uv) && Number(uv) <= Number(JSON.parse(c.value_manual)),
  bet_num: (uv, c) => {
    if (!isNumNotDefined(uv) || !c.value_manual) {
      return false;
    }

    const range =
      typeof c.value_manual === 'string'
        ? JSON.parse(c.value_manual)
        : c.value_manual;

    return Number(uv) >= Number(range.min) && Number(uv) <= Number(range.max);
  },

  not_bet_num: (uv, c) => {
    if (!isNumNotDefined(uv) || !c.value_manual) {
      return false;
    }

    const range =
      typeof c.value_manual === 'string'
        ? JSON.parse(c.value_manual)
        : c.value_manual;

    return !(
      Number(uv) >= Number(range.min) && Number(uv) <= Number(range.max)
    );
  },

  rank_is: (uv, c) => {
    if (!uv || !c.element) return false;

    const key = `${c.element.name}_${c.option_key}`;

    const rank = Number(uv[key]);

    if (Number.isNaN(rank)) return false;

    return rank === Number(c.value_manual);
  },

  rank_not_is: (uv, c) => {
    if (!uv || !c.element) return false;

    const key = `${c.element.name}_${c.option_key}`;

    const rank = Number(uv[key]);

    if (Number.isNaN(rank)) return false;

    return rank !== Number(c.value_manual);
  },

  rank_gt: (uv, c) => {
    if (!uv || !c.element) return false;

    const key = `${c.element.name}_${c.option_key}`;
    const rank = Number(uv[key]);

    if (Number.isNaN(rank)) return false;

    return rank > Number(c.value_manual);
  },

  rank_gte: (uv, c) => {
    if (!uv || !c.element) return false;

    const key = `${c.element.name}_${c.option_key}`;
    const rank = Number(uv[key]);

    if (Number.isNaN(rank)) return false;

    return rank >= Number(c.value_manual);
  },

  rank_lt: (uv, c) => {
    if (!uv || !c.element) return false;

    const key = `${c.element.name}_${c.option_key}`;
    const rank = Number(uv[key]);

    if (Number.isNaN(rank)) return false;

    return rank < Number(c.value_manual);
  },

  rank_lte: (uv, c) => {
    if (!uv || !c.element) return false;

    const key = `${c.element.name}_${c.option_key}`;
    const rank = Number(uv[key]);

    if (Number.isNaN(rank)) return false;

    return rank <= Number(c.value_manual);
  },
  rank_bet: (uv, c) => {
    if (!uv || !c.element || !c.value_manual) return false;

    const key = `${c.element.name}_${c.option_key}`;
    const rank = Number(uv[key]);

    if (Number.isNaN(rank)) return false;

    const range =
      typeof c.value_manual === 'string'
        ? JSON.parse(c.value_manual)
        : c.value_manual;

    return rank >= Number(range.min) && rank <= Number(range.max);
  },

  rank_not_bet: (uv, c) => {
    if (!uv || !c.element || !c.value_manual) return false;

    const key = `${c.element.name}_${c.option_key}`;
    const rank = Number(uv[key]);

    if (Number.isNaN(rank)) return false;

    const range =
      typeof c.value_manual === 'string'
        ? JSON.parse(c.value_manual)
        : c.value_manual;

    return !(rank >= Number(range.min) && rank <= Number(range.max));
  },

  // ================================
  // GRID SINGLE CHOICE OPERATORS
  // ================================

  // Row is exactly one option
  row_is: (uv: Record<number, number[]>, c) => {
    const rowValue = uv?.[Number(c.row_key)] ?? [];
    return OPERATOR_FUNCTIONS['is'](rowValue, c);
  },

  // Row is not option
  row_is_not: (uv: Record<number, number[]>, c) => {
    const rowValue = uv?.[Number(c.row_key)] ?? [];
    return OPERATOR_FUNCTIONS['is_not'](rowValue, c);
  },

  // ================================
  // GRID MULTIPLE CHOICE OPERATORS
  // ================================

  // Row contains any selected option
  row_inc_any: (uv: Record<number, number[]>, c) => {
    const rowValue = uv?.[Number(c.row_key)] ?? [];
    return OPERATOR_FUNCTIONS['inc_any'](rowValue, c);
  },

  // Row contains all selected options
  row_inc_all: (uv: Record<number, number[]>, c) => {
    const rowValue = uv?.[Number(c.row_key)] ?? [];
    return OPERATOR_FUNCTIONS['inc_all'](rowValue, c);
  },

  // Row does not contain any selected option
  row_not_inc_any: (uv: Record<number, number[]>, c) => {
    const rowValue = uv?.[Number(c.row_key)] ?? [];
    return OPERATOR_FUNCTIONS['not_inc_any'](rowValue, c);
  },

  // Row exactly matches options
  row_is_ex: (uv: Record<number, number[]>, c) => {
    const rowValue = uv?.[Number(c.row_key)] ?? [];
    return OPERATOR_FUNCTIONS['is_ex'](rowValue, c);
  },

  // Row does not exactly match options
  row_is_not_ex: (uv: Record<number, number[]>, c) => {
    const rowValue = uv?.[Number(c.row_key)] ?? [];
    return OPERATOR_FUNCTIONS['is_not_ex'](rowValue, c);
  },

  // Row contains one of options
  row_is_any_of: (uv: Record<number, number[]>, c) => {
    const rowValue = uv?.[Number(c.row_key)] ?? [];
    return OPERATOR_FUNCTIONS['is_any_of'](rowValue, c);
  },

  // Row does not contain any of options
  row_is_not_any_of: (uv: Record<number, number[]>, c) => {
    const rowValue = uv?.[Number(c.row_key)] ?? [];
    return OPERATOR_FUNCTIONS['is_not_any_of'](rowValue, c);
  },

  // ================================
  // GRID COUNT OPERATORS
  // ================================

  row_count_eq: (uv: Record<number, number[]>, c) => {
    const rowValue = uv?.[Number(c.row_key)] ?? [];
    return rowValue.length === Number(c.value_manual);
  },

  row_count_gt: (uv: Record<number, number[]>, c) => {
    const rowValue = uv?.[Number(c.row_key)] ?? [];
    return rowValue.length > Number(c.value_manual);
  },

  row_count_gte: (uv: Record<number, number[]>, c) => {
    const rowValue = uv?.[Number(c.row_key)] ?? [];
    return rowValue.length >= Number(c.value_manual);
  },

  row_count_lt: (uv: Record<number, number[]>, c) => {
    const rowValue = uv?.[Number(c.row_key)] ?? [];
    return rowValue.length < Number(c.value_manual);
  },

  row_count_lte: (uv: Record<number, number[]>, c) => {
    const rowValue = uv?.[Number(c.row_key)] ?? [];
    return rowValue.length <= Number(c.value_manual);
  },

  // --- State Logic ---
  ans: (uv) =>
    uv !== undefined &&
    uv !== null &&
    uv !== '' &&
    uv !== 0 &&
    !isEmptyObject(uv) &&
    !(Array.isArray(uv) && (uv.length === 0 || uv[0] === 0 || uv[0] === '')),
  nans: (uv) =>
    uv === undefined ||
    uv === null ||
    uv === '' ||
    isEmptyObject(uv) ||
    (Array.isArray(uv) && uv.length === 0),
};
